import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Shield, Leaf, Heart, Users, CheckCircle, Globe } from "lucide-react";
import { jbtBadge } from "../data/mockData";

const FadeIn = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 35 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: "easeOut" }} className={className}>
      {children}
    </motion.div>
  );
};

const EthicalStandardsPage = () => {
  const standards = [
    {
      Icon: Shield,
      title: "Provenance You Can Verify",
      desc: "Every diamond we handle is accompanied by full chain-of-custody documentation. We go beyond the Kimberley Process, implementing internal traceability protocols that track each stone from mine to market."
    },
    {
      Icon: Leaf,
      title: "Lighter Footprint, Brighter Future",
      desc: "We actively invest in reducing our environmental impact—from energy-efficient manufacturing equipment to waste-reduction programs and partnerships with responsibly managed mining operations."
    },
    {
      Icon: Heart,
      title: "People at the Center",
      desc: "Fair wages, safe conditions, and equal opportunity are not aspirations—they are requirements. Every facility in our supply chain undergoes rigorous third-party labor audits to uphold these non-negotiable standards."
    },
    {
      Icon: Users,
      title: "Strengthening Communities",
      desc: "We channel a portion of revenue into education and healthcare initiatives in the communities connected to our supply chain, because lasting value should extend far beyond the jewelry itself."
    },
    {
      Icon: Globe,
      title: "Vetted Partnerships Only",
      desc: "We work exclusively with suppliers who share our ethical convictions. Each partner is evaluated through a multi-stage due diligence process covering labor practices, environmental impact, and business integrity."
    },
    {
      Icon: CheckCircle,
      title: "Independently Certified",
      desc: "Our practices are validated by recognized industry bodies. We maintain active membership in the Jewelers Board of Trade and adhere to standards that exceed baseline regulatory requirements."
    }
  ];

  const commitments = [
    "Kimberley Process Certification Scheme compliance",
    "Jewelers Board of Trade (JBT) verified member",
    "Full chain-of-custody diamond documentation",
    "Third-party facility audits conducted annually",
    "Zero tolerance for forced or child labor",
    "Energy-efficient manufacturing processes",
    "Transparent sustainability reporting",
    "Community reinvestment programs"
  ];

  return (
    <div>
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center bg-[#1a1a1a]">
        <div className="absolute inset-0 bg-black/40" />
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }} className="relative z-10 text-center px-6">
          <h1 className="text-4xl md:text-5xl lg:text-6xl text-white font-light"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Ethical Standards
          </h1>
          <p className="mt-4 text-white/60 text-sm tracking-[0.1em]"
            style={{ fontFamily: "'Inter', sans-serif" }}>
            Integrity woven into every facet
          </p>
          <div className="h-[1px] w-16 bg-[#c8a96e] mx-auto mt-6" />
        </motion.div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-[#faf9f7]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 text-center">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl text-[#1a1a1a] font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Accountability at Every Level
            </h2>
            <p className="mt-6 text-[#555] text-[15px] leading-[1.8] max-w-2xl mx-auto"
              style={{ fontFamily: "'Inter', sans-serif" }}>
              At Astron Diamond, ethics are not a marketing checkbox—they are the foundation on
              which our entire business is built. From the mines that yield our rough diamonds to
              the workshops where they are transformed, every link in our supply chain operates
              under the same uncompromising standards of transparency, fairness, and environmental
              stewardship.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Standards Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {standards.map((item, i) => (
              <FadeIn key={item.title} delay={i * 0.08}>
                <div className="p-8 bg-[#faf9f7] hover:bg-[#1a1a1a] group transition-all duration-500">
                  <item.Icon className="w-8 h-8 text-[#c8a96e] mb-6" strokeWidth={1.5} />
                  <h3 className="text-xl text-[#1a1a1a] group-hover:text-white transition-colors"
                    style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {item.title}
                  </h3>
                  <p className="mt-4 text-[#888] group-hover:text-white/60 text-[14px] leading-relaxed transition-colors"
                    style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item.desc}
                  </p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Commitments */}
      <section className="py-24 bg-[#1a1a1a]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl text-white font-light"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Our Pledges
              </h2>
            </div>
          </FadeIn>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {commitments.map((item, i) => (
              <FadeIn key={item} delay={i * 0.05}>
                <div className="flex items-center gap-3 p-5 border border-white/10 hover:border-[#c8a96e]/30 transition-all duration-300">
                  <CheckCircle className="w-5 h-5 text-[#c8a96e] flex-shrink-0" />
                  <span className="text-white/70 text-[14px]" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {item}
                  </span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* JBT Badge */}
      <section className="py-20 bg-[#faf9f7]">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10">
          <FadeIn>
            <div className="text-center">
              <h2 className="text-2xl text-[#1a1a1a] font-light mb-8"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                Verified & Accredited
              </h2>
              <div className="flex justify-center items-center gap-4">
                <img
                  src={jbtBadge.logo}
                  alt={jbtBadge.name}
                  className="h-32 w-auto object-contain"
                />
                <span className="text-[#555] text-[13px] tracking-[0.12em] uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}>
                  {jbtBadge.name}
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default EthicalStandardsPage;
